import { createContext, useContext, useEffect, useState } from 'react';

import { api } from '../../../api';
import { Enrolment, SubscriptionEnrolments } from '../../../api/types/enrolment';
import { Session } from '../../../api/types/session';
import { Student } from '../../../api/types/student';
import useBillingInformationQuery from '../../../hooks/queries/useBillingInformationQuery.js';
import useNextPaymentQuery from '../../../hooks/queries/useNextPaymentQuery';
import { ENROLMENT_STATUS } from '../../../types/hubEnums';
import { BillingInformationType } from '../../../types/hubPropTypes';
import { BillingQuery } from '../../../types/hubTypes';
import { formatError } from '../../../util/errors';

export enum SUBSCRIPTION_TYPE {
  BUNDLED = 'Bundled',
  TRIAL = 'Trial',
  CLUEY_PLUS = 'Cluey+',
}

export enum CONFIRMATION_TYPE {
  CLUEY_PLUS_DISCOUNT_APPLIED = 'CLUEY_PLUS_DISCOUNT_APPLIED',
  CLUEY_PLUS_SUBSCRIPTION_CREATED = 'CLUEY_PLUS_SUBSCRIPTION_CREATED',
  CLUEY_PLUS_SUBSCRIPTION_CANCELLED = 'CLUEY_PLUS_SUBSCRIPTION_CANCELLED',
}

export enum CANCELLATION_TYPE {
  CANCEL_TUTORIAL = 'CANCEL_TUTORIAL',
  CANCEL_CLUEY_PLUS = 'CANCEL_CLUEY_PLUS',
}

export type SubscriptionContextType = {
  clueyPlus: {
    isLoading: boolean;
    nextPayment: BillingQuery | null;
    subscription: SubscriptionEnrolments | null;
    cancellation: {
      show: boolean;
      setShow: (show: boolean) => void;
      cancellationType: CANCELLATION_TYPE | null;
    };
    details: {
      show: boolean;
      setShow: (show: boolean) => void;
    };
    specialOffer: {
      show: boolean;
      setShow: (show: boolean) => void;
    };
    toast: {
      message: React.ReactNode;
      setMessage: (message: React.ReactNode) => void;
      show: boolean;
      setShow: (show: boolean) => void;
    };
    confirmation: {
      show: boolean;
      setShow: (show: boolean) => void;
      confirmationType: CONFIRMATION_TYPE | null;
    };
    showDetailsDialog: () => void;
    showSpecialOfferDialog: () => void;
    showCancellationDialog: (cancellationType: CANCELLATION_TYPE) => void;
    showConfirmationDialog: (confirmationType: CONFIRMATION_TYPE) => void;
  };
  banner: {
    show: boolean;
    setShow: (show: boolean) => void;
    message: React.ReactNode;
    setMessage: (message: React.ReactNode) => void;
  };
  billing: {
    info: {
      billingInformation: BillingInformationType;
      isLoading: boolean;
      error: string | null;
    };
  };
  student: {
    students: Student[];
    studentError: string | null;
    studentIsLoading: boolean;
    selectedStudent: Student | null;
    setSelectedStudent: (student: Student | null) => void;
    selectedStudentEnrolments: Enrolment[];
  };
  enrolments: {
    active: {
      enrolments: Enrolment[];
      isLoading: boolean;
      error: string | null;
    };
  };
  sessions: {
    upcoming: {
      sessions: Session[];
      isLoading: boolean;
      error: string | null;
    };
    completed: {
      sessions: Session[];
      isLoading: boolean;
      error: string | null;
    };
  };
};

export const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const SubscriptionContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedStudentEnrolments, setSelectedStudentEnrolments] = useState<Enrolment[]>([]);
  const [bannerShow, setBannerShow] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<React.ReactNode>(null);

  const {
    data: billingInformation,
    isLoading: isLoadingBillingInformation,
    error: getBillingInformationError,
  } = useBillingInformationQuery();

  // ClueyPlus related state
  const [isLoadingClueyPlus, setIsLoadingClueyPlus] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionEnrolments | null>(null);
  const [showSpecialOfferMessage, setShowSpecialOfferMessage] = useState(false);
  const [toastMessage, setToastMessage] = useState<React.ReactNode>(null);
  const [toastShow, setToastShow] = useState(false);
  const [detailsShow, setDetailsShow] = useState(true);
  const [cancellationShow, setCancellationShow] = useState(false);
  const [cancellationType, setCancellationType] = useState<CANCELLATION_TYPE | null>(null);
  const [confirmationShow, setConfirmationShow] = useState(false);
  const [confirmationType, setConfirmationType] = useState<CONFIRMATION_TYPE | null>(null);
  const [nextPayment, setNextPayment] = useState<BillingQuery | null>(null);

  const { data: nextPaymentData, isLoading: isLoadingNextPayment } = useNextPaymentQuery();

  const { data: studentsData, isLoading: isLoadingStudents, error: studentError } = api.student.all.useQuery();

  const { data: subscriptionEnrolments, isLoading: isLoadingSubscriptionEnrolments } =
    api.enrolment.subscriptionEnrolments.useQuery();

  const {
    data: activeEnrolments,
    isLoading: isLoadingActiveEnrolments,
    error: activeEnrolmentsError,
  } = api.enrolment.all.useQuery('active');

  const {
    data: upcomingSessions,
    isLoading: isLoadingUpcomingSessions,
    error: upcomingSessionsError,
  } = api.session.all.useQuery('upcoming');

  const {
    data: completedSessions,
    isLoading: isLoadingCompletedSessions,
    error: completedSessionsError,
  } = api.session.all.useQuery('completed');

  useEffect(() => {
    if (studentsData && !isLoadingStudents) {
      setStudents(
        studentsData.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        }) ?? []
      );
    }
  }, [studentsData, isLoadingStudents, students]);

  useEffect(() => {
    if (!isLoadingNextPayment && nextPaymentData) {
      setNextPayment(nextPaymentData);
    }
  }, [nextPaymentData, isLoadingNextPayment]);

  useEffect(() => {
    if (!isLoadingSubscriptionEnrolments) {
      setIsLoadingClueyPlus(false);
    }
  }, [isLoadingSubscriptionEnrolments]);

  // Set the subscription for the selected student
  useEffect(() => {
    if (!isLoadingSubscriptionEnrolments && subscriptionEnrolments && selectedStudent) {
      setSubscription(
        subscriptionEnrolments.enrolments.find((enrolment) => enrolment.studentId === selectedStudent?.studentId) ??
          null
      );
    }
  }, [isLoadingSubscriptionEnrolments, subscriptionEnrolments, selectedStudent, setSubscription]);

  // Set the active enrolments for the selected student
  useEffect(() => {
    if (selectedStudent) {
      setSelectedStudentEnrolments(
        activeEnrolments.filter((enrolment) => enrolment.studentSfid === selectedStudent?.studentId)
      );
    }
  }, [activeEnrolments, selectedStudent, setSelectedStudentEnrolments]);

  // Checks if the subscription type is bundled and the student has any cancelled requested enrolments or no enrolments at all
  // Then show the special offer message on load of subscription details page
  useEffect(() => {
    if (
      activeEnrolments &&
      !isLoadingActiveEnrolments &&
      selectedStudent &&
      subscription?.subscriptionType === SUBSCRIPTION_TYPE.BUNDLED
    ) {
      const studentActiveEnrolments = activeEnrolments.filter(
        (enrolment) => enrolment.studentSfid === selectedStudent?.studentId
      );
      const hasCancelledRequestedEnrolment = studentActiveEnrolments.some(
        (enrolment) => enrolment.enrolmentStatus === ENROLMENT_STATUS.CANCEL_REQUESTED
      );

      setShowSpecialOfferMessage(
        (studentActiveEnrolments && studentActiveEnrolments?.length === 0) || hasCancelledRequestedEnrolment
      );
    }
  }, [activeEnrolments, isLoadingActiveEnrolments, selectedStudent, subscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        banner: {
          show: bannerShow,
          setShow: setBannerShow,
          message: bannerMessage,
          setMessage: setBannerMessage,
        },
        billing: {
          info: {
            billingInformation,
            isLoading: isLoadingBillingInformation,
            error: getBillingInformationError ? formatError(getBillingInformationError) : null,
          },
        },
        clueyPlus: {
          isLoading: isLoadingClueyPlus,
          nextPayment,
          subscription,
          // Cancellation confirmation dialogs
          cancellation: {
            show: cancellationShow,
            setShow: setCancellationShow,
            cancellationType,
          },
          // Student enrolment details dialog
          details: {
            show: detailsShow,
            setShow: setDetailsShow,
          },
          // Special offer dialog
          specialOffer: {
            show: showSpecialOfferMessage,
            setShow: setShowSpecialOfferMessage,
          },
          // Toast message for success/error messages
          toast: {
            message: toastMessage,
            setMessage: setToastMessage,
            show: toastShow,
            setShow: setToastShow,
          },
          // Confirmation dialog
          confirmation: {
            show: confirmationShow,
            setShow: setConfirmationShow,
            confirmationType,
          },
          showDetailsDialog: () => {
            setDetailsShow(true);
            setCancellationShow(false);
            setShowSpecialOfferMessage(false);
            setConfirmationShow(false);

            setConfirmationType(null);
            setCancellationType(null);
          },
          showSpecialOfferDialog: () => {
            setShowSpecialOfferMessage(true);
            setDetailsShow(false);
            setCancellationShow(false);
            setConfirmationShow(false);
          },
          showCancellationDialog: (cancellationType: CANCELLATION_TYPE) => {
            setCancellationType(cancellationType);
            setCancellationShow(true);
            setDetailsShow(false);
            setShowSpecialOfferMessage(false);
            setConfirmationShow(false);
          },
          showConfirmationDialog: (confirmationType: CONFIRMATION_TYPE) => {
            setConfirmationType(confirmationType);
            setConfirmationShow(true);
            setDetailsShow(false);
            setCancellationShow(false);
            setShowSpecialOfferMessage(false);
          },
        },
        student: {
          students,
          studentIsLoading: isLoadingStudents,
          studentError: studentError ? formatError(studentError) : undefined,
          selectedStudentEnrolments,
          selectedStudent,
          setSelectedStudent,
        },
        enrolments: {
          active: {
            enrolments: activeEnrolments ?? [],
            isLoading: isLoadingActiveEnrolments,
            error: activeEnrolmentsError ? formatError(activeEnrolmentsError) : null,
          },
        },
        sessions: {
          upcoming: {
            sessions: upcomingSessions ?? [],
            isLoading: isLoadingUpcomingSessions,
            error: upcomingSessionsError ? formatError(upcomingSessionsError) : null,
          },
          completed: {
            sessions: completedSessions ?? [],
            isLoading: isLoadingCompletedSessions,
            error: completedSessionsError ? formatError(completedSessionsError) : null,
          },
        },
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const withSubscriptionContextProvider = (Component: React.ComponentType<any>) => {
  const WrappedComponent = (props: any) => (
    <SubscriptionContextProvider>
      <Component {...props} />
    </SubscriptionContextProvider>
  );
  return WrappedComponent;
};

export default function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionContextProvider');
  }
  return context;
}
